/**
 * Effective OSD settings: code defaults (viewerConfig.js) ← organisation ← user.
 * Layers and validation are described in docs/0006_osd-viewer-port.md (Settings model);
 * the backend already strips user keys the user may not set.
 */
import {store} from '../../../helper/store';
import {OSD_CONFIG} from './viewerConfig';
import {getZoomScaleForSlide, isImportedSlide, nominalObjectiveForMpp} from '../utils/map_utils';

export const DIGITAL_ZOOM_MODES = {AUTO: 'auto', PHYSICAL_TOP: 'physical_top', VIRTUAL: 'virtual'};
export const SCAN_KINDS = {MORPHLE: 'morphle', IMPORTED: 'imported'};

/** Tuning keys whose OSD_CONFIG location differs from the key name. */
const NESTED_TUNING_KEYS = {
    navigatorMinSize: ['navigatorResize', 'minSize'],
    navigatorMaxRatio: ['navigatorResize', 'maxRatio'],
};

const OBJECTIVE_KEYS = ['twenty_x', 'forty_x', 'sixty_x', 'hundred_x'];
const OBJECTIVE_KEY_FOR_NOMINAL = {20: 'twenty_x', 40: 'forty_x', 60: 'sixty_x', 100: 'hundred_x'};

export const getOsdViewerConfig = (state = store.getState()) => state.osdViewerConfigReducer || {};

let tuningFor;
let tunedConfig = OSD_CONFIG;

/** OSD_CONFIG with the organisation `tuning` applied; cached per tuning object. */
export const getTunedOsdConfig = (osdViewerConfig = getOsdViewerConfig()) => {
    const tuning = osdViewerConfig.organisation?.tuning;
    if (tuning === tuningFor) return tunedConfig;
    tuningFor = tuning;
    tunedConfig = {...OSD_CONFIG, navigatorResize: {...OSD_CONFIG.navigatorResize}};
    Object.entries(tuning || {}).forEach(([key, value]) => {
        if (typeof value !== 'number') return;
        const nested = NESTED_TUNING_KEYS[key];
        if (nested) tunedConfig[nested[0]][nested[1]] = value;
        else if (key in OSD_CONFIG) tunedConfig[key] = value;
    });
    return tunedConfig;
};

export const getSensitivity = (osdViewerConfig = getOsdViewerConfig()) => ({
    wheel: 1,
    pan: 1,
    ...(osdViewerConfig.organisation?.sensitivity || {}),
    ...(osdViewerConfig.user?.sensitivity || {}),
});

export const getScanKind = (slide) => isImportedSlide(slide) ? SCAN_KINDS.IMPORTED : SCAN_KINDS.MORPHLE;

/** Mapping key for a slide, using the same fallbacks as the magnification labels. */
export const getObjectiveKey = (slide) => {
    const type = String(slide?.objective_type || '').toLowerCase();
    if (OBJECTIVE_KEYS.includes(type)) return type;
    if (isImportedSlide(slide)) return OBJECTIVE_KEY_FOR_NOMINAL[nominalObjectiveForMpp(slide?.uperpixel)] || 'forty_x';
    return 'forty_x';
};

/**
 * Imported slides have no physical digital level, so they always upscale virtually. Morphle scans
 * use the physical top level unless the organisation picked `virtual`.
 */
export const getDigitalZoomMode = (slide, osdViewerConfig = getOsdViewerConfig()) => {
    if (isImportedSlide(slide)) return DIGITAL_ZOOM_MODES.VIRTUAL;
    return osdViewerConfig.organisation?.digital_zoom_mode === DIGITAL_ZOOM_MODES.VIRTUAL
        ? DIGITAL_ZOOM_MODES.VIRTUAL : DIGITAL_ZOOM_MODES.PHYSICAL_TOP;
};

/** Highest stitched level OSD may fetch; above it OSD upscales. Morphle `virtual` skips the top level. */
export const getServedLevelCap = (slide, zoomLevels, osdViewerConfig = getOsdViewerConfig()) =>
    !isImportedSlide(slide) && getDigitalZoomMode(slide, osdViewerConfig) === DIGITAL_ZOOM_MODES.VIRTUAL
        ? zoomLevels.length - 2 : zoomLevels.length - 1;

/** Same key format as the backend's `mag_key` (`f"{float(v):g}"`). */
export const magKey = (value) => String(Number(value));

export const getMagMapping = (slide, osdViewerConfig = getOsdViewerConfig()) =>
    osdViewerConfig.organisation?.mag_mapping?.[getScanKind(slide)]?.[getObjectiveKey(slide)] || {};

/** Labels per zoom index after the organisation's magnification mapping. */
export const getMappedZoomScale = (slide, zoomLevels, osdViewerConfig = getOsdViewerConfig()) => {
    const mapping = getMagMapping(slide, osdViewerConfig);
    return getZoomScaleForSlide(slide, zoomLevels).map((label) => mapping[magKey(label)] ?? label);
};
